import React from "react";
import { cn, Typography } from "@semo/shared-ui";
import { PartyAvatar } from "../atoms/PartyAvatar";
import { MessageStatus } from "../atoms/MessageStatus";
import {
  CARD_SIZE_CLASSES,
  type MessageCardSize,
} from "./MessageCardWrapper";

/**
 * MessageLayout - 공통 채팅 메시지 컨테이너
 *
 * 모든 메시지 타입(텍스트, 견적, 계약 등)에서 공통으로 사용하는
 * 레이아웃(정렬, 아바타, 이름, 시간/상태 표시)을 담당합니다.
 */

// text: 말풍선은 내용 폭(shrink-to-fit)에 맞추되 85% 를 넘지 않는다.
// card: 행 자체를 w-full 로 확정해야 MessageCardWrapper 의 w-full 이 해석된다.
//       max-w-full 만 주면 행 폭이 indefinite (fit-content) 라 카드가 하한 없이
//       내용 길이대로 쪼그라든다 (견적 금액 자릿수/품목명/버튼 라벨에 따라 폭이 달라짐).
//       카드의 실제 폭 상한은 MessageCardWrapper 가 담당한다.
const MESSAGE_WIDTH_CLASSES = {
  text: "max-w-[85%] sm:max-w-[85%]",
  card: "w-full",
} as const;

export interface MessageLayoutProps {
  /** 메시지 발신 방향 */
  sender: "me" | "other";
  /** 상대방 이름 */
  partyName: string;
  /** 메시지 생성 시간 (표시용 문자열) */
  time: string;
  /** 읽음 여부 */
  isRead: boolean;
  /** 전송 중 여부 */
  isPending?: boolean;
  /** 전송 실패 여부 */
  isError?: boolean;
  /** 삭제 여부 */
  isDeleted?: boolean;
  /** 아바타 표시 여부 */
  showAvatar?: boolean;
  /** 이름 표시 여부 */
  showName?: boolean;
  /** 너비 타입 (text: 내용 폭 · 최대 85%, card: 행 전체 폭 · 상한은 MessageCardWrapper) */
  variant?: "text" | "card";
  /**
   * card variant 에서 본문 칼럼에 씌울 폭 상한.
   * 안에 넣는 MessageCardWrapper 의 size 와 반드시 같은 값을 줄 것.
   */
  cardSize?: MessageCardSize;
  /** 그룹의 첫 번째 메시지 여부 */
  isFirstInGroup?: boolean;
  /** 그룹의 마지막 메시지 여부 */
  isLastInGroup?: boolean;
  /** 시간 표시 여부 */
  showTime?: boolean;
  /** 재전송 핸들러 */
  onRetry?: () => void;
  /** 메시지 본문 (Bubble 또는 Card) */
  children: React.ReactNode;
  /** 추가 클래스명 */
  className?: string;
}

export const MessageLayout: React.FC<MessageLayoutProps> = ({
  sender,
  partyName,
  time,
  isRead,
  isPending = false,
  isError = false,
  isDeleted = false,
  showAvatar = true,
  showName = true,
  isLastInGroup = true,
  showTime = true,
  variant = "text",
  cardSize = "default",
  onRetry,
  children,
  className,
}) => {
  const widthClass = MESSAGE_WIDTH_CLASSES[variant];

  // card variant 는 행이 w-full 이라 본문 칼럼(flex-1)이 남는 폭을 전부 먹는다.
  // 카드 자체는 MessageCardWrapper 의 max-w-* 에서 멈추므로, 상한을 씌워두지 않으면
  // 그 차이만큼 시간/읽음이 카드에서 떨어져 채팅 영역 끝으로 밀려난다.
  // 칼럼에 같은 상한을 주면 폭은 여전히 확정(w-full)이라 #750 의 카드 폭 회귀도 없다.
  const contentWidthClass =
    variant === "card" ? CARD_SIZE_CLASSES[cardSize] : undefined;

  // Dynamic margin: Grouped messages (not last) have smaller gap (mb-1), Last in group has larger gap (mb-4)
  const marginBottom = isLastInGroup ? "mb-4" : "mb-1";

  return (
    <div
      data-slot="chat-message-layout"
      className={cn(
        "flex px-1 sm:px-0",
        marginBottom,
        sender === "me" ? "justify-end" : "justify-start",
        className,
      )}
    >
      <div
        className={cn(
          "flex items-end gap-1.5 sm:gap-2",
          widthClass,
          sender === "me" && "flex-row-reverse",
        )}
      >
        {/* Avatar */}
        {showAvatar ? (
          sender === "other" ? (
            <PartyAvatar
              name={partyName}
              size="sm"
              className="border border-slate-200 shrink-0 mb-0.5"
            />
          ) : (
            <PartyAvatar
              name="익명"
              size="md"
              isAnonymous
              className="border-2 border-primary/30 shadow-sm shrink-0 mb-0.5"
            />
          )
        ) : (
          <div className="w-10 h-10 shrink-0" />
        )}

        <div
          className={cn(
            "min-w-0 flex-1 flex flex-col",
            contentWidthClass,
            sender === "me" ? "items-end" : "items-start",
            isPending && "opacity-50 transition-opacity duration-200",
          )}
        >
          {/* Name (상대방만 표시) */}
          {sender === "other" && showName && (
            <Typography
              variant="caption"
              color="muted"
              className="mb-1 ml-1 truncate max-w-full"
            >
              {partyName}
            </Typography>
          )}

          {/* Message Payload (Bubble or Card) */}
          <div className={cn(variant === "card" ? "w-full" : "w-fit")}>
            {children}
          </div>
        </div>

        {/* Message Status (Time, Read, Retry) */}
        <MessageStatus
          time={time}
          showTime={showTime}
          isRead={isRead}
          isPending={isPending}
          isError={isError}
          isDeleted={isDeleted}
          sender={sender}
          onRetry={onRetry}
        />
      </div>
    </div>
  );
};
